<template>
  <div
    v-if="useRebatePermission || giveRebatePermission"
    v-loading="loading"
    class="use-rebate-block"
  >
    <BaseElForm
      ref="formRef"
      label-position="left"
      label-width="100px"
      :model="formData"
      :rules="formRules"
    >
      <BaseElFormItem v-if="isOrgEnabled" :label="$t('member.tab.rebate.useRebateBlock.operation')">
        <BaseElSelect disabled :value="'org'">
          <BaseElSelectOption value="org" :label="$t('member.tab.rebate.useRebateBlock.orgRebate')" />
        </BaseElSelect>
      </BaseElFormItem>
      <BaseElFormItem :label="$t('member.tab.rebate.useRebateBlock.operation')" prop="type">
        <BaseElRadioGroup v-model="formData.type" @change="onTypeChange">
          <BaseElRadio v-if="useRebatePermission" label="use">{{ $t('member.tab.rebate.useRebateBlock.deduct') }}</BaseElRadio>
          <BaseElRadio v-if="giveRebatePermission" label="add">{{ $t('member.tab.rebate.useRebateBlock.gift') }}</BaseElRadio>
        </BaseElRadioGroup>
      </BaseElFormItem>
      <BaseElFormItem :label="$t('member.tab.rebate.useRebateBlock.amount')" prop="amount">
        <BaseElInput v-model="formData.amount" />
      </BaseElFormItem>
      <BaseElFormItem :label="$t('member.tab.rebate.useRebateBlock.remark')" prop="commnet">
        <BaseElInput v-model="formData.comment" />
      </BaseElFormItem>
      <BaseElFormItem>
        <div>
          <BaseElButton @click="reset">{{ $t('member.tab.rebate.useRebateBlock.cancel') }}</BaseElButton>
          <BaseElButton type="primary" @click="handleRebateWallet">{{ $t('member.tab.rebate.useRebateBlock.save') }}</BaseElButton>
        </div>
      </BaseElFormItem>
    </BaseElForm>
  </div>
</template>

<script setup>
import { reactive, ref, computed } from 'vue'
import { maxNumberRules, minRules, noEmptyRules, rangeRules } from '@/validation'
import { AddMemberRebateWalletRecord, UseMemberRebateWalletRecord } from '@/api/rebate'
import store from '@/store'
import formUtils from '@/utils/form'
import { debounce } from 'lodash'
import { usePermissions } from '@/use/permissions'

const props = defineProps({
  memberId: {
    type: String,
  },
  walletBalance: {
    type: [String, Number],
    default: 0,
  },
  isOrgEnabled: {
    type: Boolean,
    default: false,
  },
})
const emit = defineEmits(['refresh'])
const { checkAction } = usePermissions()

const useRebatePermission = computed(() => {
  return checkAction('admin.member.useCashbackWalletRecord')
})
const giveRebatePermission = computed(() => {
  return checkAction('admin.member.addCashbackWalletRecord')
})

const loading = ref(false)
const formRef = ref(null)
const shopId = computed(() => store.getters.shop)
const formData = reactive({
  type: null,
  amount: null,
  comment: null,
})

const formRules = computed(() => {
  const rules = {
    type: [noEmptyRules()],
    amount: [noEmptyRules()],
  }
  if (formData.type === 'use') {
    if (!props.walletBalance) {
      rules.amount.push(minRules(0))
      rules.amount.push(maxNumberRules(0))
    } else {
      rules.amount.push(rangeRules(1, props.walletBalance))
    }
  } else if (formData.type === 'add') {
    rules.amount.push(minRules(1))
  }
  return rules
})

const reset = () => {
  formData.type = null
  formData.amount = null
  formData.comment = null
  setTimeout(() => {
    formUtils.clearValidate(formRef.value)
  }, 30)
}

const handleRebateWallet = debounce(async () => {
  if (loading.value) return
  loading.value = true
  if (!(await formUtils.checkForm(formRef.value))) {
    loading.value = false
    return
  }
  const type = formData.type
  const methods = {
    add: AddMemberRebateWalletRecord,
    use: UseMemberRebateWalletRecord,
  }
  const [, err] = await methods[type]({
    shopId: shopId.value,
    memberId: props.memberId,
    originMessage: formData.comment || undefined,
    addAmount: type === 'add' ? formData.amount : undefined,
    useAmount: type === 'use' ? formData.amount : undefined,
  })
  loading.value = false
  if (err) return (window.$message.error(err))

  setTimeout(() => {
    formUtils.clearValidate(formRef.value)
  }, 30)

  emit('refresh')
  reset()
}, 300, { leading: false, trailing: true })

const onTypeChange = () => {
  setTimeout(() => {
    formUtils.clearValidate(formRef.value)
  }, 30)
}
</script>

<style scoped lang="postcss">

</style>
